import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    FormGroup,
    Loader,
    PageHeader,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEpisodeCategory } from '@common/interfaces';
import type { IFundingLog } from '@common/interfaces/api/fundingLogsApi';
import type { IFundingLogSearch } from '@common/validation/fundingLogSearch';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { fundingLogsSearchSchema } from '@common/validation/fundingLogSearch';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { translateKey } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';

const baseUrl = '/api/finance/fundingLogs';

export function FundingLogs() {
    const abortableFetch = useAbortableFetch();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSearching, setIsSearching] = useState(false);
    const [patientId, setPatientId] = useState<string | undefined>();
    const [episodeCategories, setEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [episodeCategoryId, setEpisodeCategoryId] = useState<string | undefined>();
    const [fundingLogs, setFundingLogs] = useState<IFundingLog[]>([]);

    useEffect(() => {
        loadEpisodeCategories();
    }, []);

    assertHasAuthorization(ManagerAuthorization.fundingLogRead);

    return (
        <PageTemplate>
            <PageHeader title={translateKey('fundingLog.fundingLog')} />
            <Box
                alignItems="start"
                display="grid"
                gap={3}
                gridTemplateColumns={['1fr', null, '1fr 1fr max-content', '320px 320px max-content']}
                mb={4}
            >
                <FormGroup layout="vertical">
                    <TextInput
                        error={getValidationErrorTranslation(validationErrors.get('patientId'))}
                        onBlur={() => validateOne('patientId', patientId)}
                        onChange={(e) => setPatientId(e.target.value)}
                        placeholder={translateKey('fundingLog.patientId')}
                        value={patientId ?? ''}
                    />
                </FormGroup>
                <FormGroup layout="vertical">
                    <Select
                        error={getValidationErrorTranslation(validationErrors.get('episodeCategoryId'))}
                        getOptionLabel={({ name }) => translateKey(`episodeCategoryEnum.${name}`)}
                        getOptionValue={({ id }) => id}
                        onChange={(option) => {
                            setEpisodeCategoryId(option?.id);
                            validateOne('episodeCategoryId', option?.id);
                        }}
                        options={episodeCategories}
                        placeholder={translateKey('episodeCategory.episodeCategory')}
                    />
                </FormGroup>
                <Button
                    isLoading={isSearching}
                    onClick={() => getAll()}
                    variant="solid"
                >
                    {translateKey('common.search')}
                </Button>
            </Box>

            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('fundingLog.performedDatetime')}</TableHeader>
                            <TableHeader>{translateKey('fundingLog.fundingStep')}</TableHeader>
                            <TableHeader>{translateKey('fundingLog.data')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    {!isSearching && (
                        <TableBody>
                            {fundingLogs.map((fundingLog) => (
                                <TableRow key={`fundingLog-${fundingLog.id}`}>
                                    <TableCell>
                                        {DateTime.from(fundingLog.performedDatetime).format('DD-MM-YYYY HH:mm:ss.SSS')}
                                    </TableCell>
                                    <TableCell>{fundingLog.fundingStep}</TableCell>
                                    <TableCell>
                                        <pre id="json">{JSON.stringify(fundingLog.data, null, 2)}</pre>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
            {isSearching && (
                <Loader
                    className="mt-3 mx-auto"
                    size={'large'}
                />
            )}
        </PageTemplate>
    );

    async function loadEpisodeCategories() {
        const episodeCategories = (await abortableFetch.getJson('/api/episodeCategories?isActive=true')).json
            .items as IEpisodeCategory[];

        episodeCategories.sort((a, b) =>
            translateKey(`episodeCategoryEnum.${a.name}`).localeCompare(translateKey(`episodeCategoryEnum.${b.name}`))
        );

        setEpisodeCategories(episodeCategories);
    }

    async function getAll() {
        const { errors } = validate(fundingLogsSearchSchema, { episodeCategoryId, patientId });
        if (errors.size > 0) {
            setValidationErrors(errors);

            return;
        }
        setIsSearching(true);

        const url = `${baseUrl}?patientId=${patientId}&episodeCategoryId=${episodeCategoryId}`;
        const items = (await abortableFetch.getJson(url))?.json?.items;
        setFundingLogs([...items]);
        setIsSearching(false);
    }

    function validateOne(key: keyof IFundingLogSearch, value: string | undefined) {
        setValidationErrors(
            updateValidationErrorsForOne(fundingLogsSearchSchema, { [key]: value }, validationErrors, key)
        );
    }
}
